import { ref } from 'vue'
import { defineStore } from 'pinia'
import Swal from 'sweetalert2'
import { useCart } from './Cart'
import { useLocales } from './Locale'

export const useShipment = defineStore('useShipment', () => {
    const shipping_note = ref<string>('')
    const shipping_address = ref<any>([])
    const selected_address = ref<any>(null)
    const idAddress = ref<number>(0)
    const modalState = ref<string>('')
    const is_primary = ref<string>('')
    const address_note = ref<string>('')
    const recipient_name = ref<string>('')
    const email = ref<string>('')
    const phone = ref<string>('')
    const address = ref<string>('')
    const address_detail = ref<string>('')
    const country = ref<string>('')
    const province = ref<string>('')
    const province_id = ref<string>('')
    const city = ref<string>('')
    const city_id = ref<string>('')
    const postal_code = ref<string>('')
    const area_map = ref<any>(null)
    const area_list = ref<any>([])
    const province_list = ref<any>([])
    const city_list = ref<any>([])
    const expedition_list = ref<any>([])
    const selected_expedition = ref('')

    const shipping_information = ref<any>(null)

    const getShippingInformation = async () => {
        try {
            const response = await window.axios.get(
                `/api/page/shipping?lang=${useLocales().isLang}`
            )
            shipping_information.value = response.data.data
        } catch (error) {
            console.log(error)
        }
    }

    const getAddresses = async () => {
        try {
            const response = await window.axios.get('/api/address')
            shipping_address.value = response.data.data
        } catch (error) {
            console.log(error)
        }
    }

    const getProvince = async () => {
        try {
            const response = await window.axios.get('/api/province')
            province_list.value = response.data.rajaongkir.results
        } catch (error) {
            console.log(error)
        }
    }

    const getCity = async () => {
        try {
            const response = await window.axios.get(
                `/api/city/${province_id.value}`
            )
            city_list.value = response.data.rajaongkir.results
        } catch (error) {
            console.log(error)
        }
    }

    const getAreaMap = async (input: string) => {
        console.log('getAreaMap called with input:', input)
        // convert input space to +
        input = input.replace(/\s+/g, '+')
        try {
            const response = await window.axios.get(`/api/v2/location?input=${input}`)
            console.log('getAreaMap response:', response.data)
            area_list.value = response.data.areas
        } catch (error) {
            console.log('getAreaMap error:', error)
            area_list.value = []
        }
    }

    const setAddressDefault = async (order: boolean) => {
        await getAddresses()
        const primaryAddress = shipping_address.value.find(
            (address: any) => address.is_primary === true
        )

        let selectedAddress = primaryAddress

        // If no primary address exists, try to find the first valid address
        if (!primaryAddress && shipping_address.value.length > 0) {
            // For Indonesian addresses, find one with area_id
            selectedAddress = shipping_address.value.find((address: any) => {
                if (address.country === 'Indonesia') {
                    return address.area_id && address.area_id !== ''
                }
                return true // Non-Indonesian addresses are always valid
            })
        }

        // For Indonesian addresses, only accept those with area_id
        if (selectedAddress && selectedAddress.country === 'Indonesia' &&
            (!selectedAddress.area_id || selectedAddress.area_id === '')) {
            selected_address.value = null
            useCart().haveAddress = false

            if (order) {
                form_set('update', selectedAddress.id)
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid Address',
                    text: 'Your address is missing area information required for shipping. Please update it to continue.',
                    showConfirmButton: true,
                })
            }
            return
        }

        selected_address.value = selectedAddress ?? null

        if (order) {
            if (shipping_address.value.length > 0 && selectedAddress) {
                useCart().haveAddress = true

                if (selected_address.value.country == 'Indonesia') {
                    // For Indonesian addresses, only proceed if area_id exists
                    if (selected_address.value.area_id && selected_address.value.area_id !== '') {
                        await getExpedition()
                    } else {
                        // This should not happen due to the check above, but added for safety
                        useCart().haveAddress = false
                        form_set('update', selected_address.value.id)
                        Swal.fire({
                            icon: 'error',
                            title: 'Missing Area Information',
                            text: 'Indonesian addresses require area information for shipping. Please update your address.',
                            showConfirmButton: true,
                        })
                        return
                    }
                } else {
                    // For non-Indonesian addresses, proceed normally
                    await getExpedition()
                }
            } else {
                useCart().haveAddress = false
            }

            if (useLocales().isLang == 'en') {
                useCart().paymentMethod = 'paypal'
            } else {
                useCart().paymentMethod = 'bca'
            }
        }
    }

    const clearAddress = () => {
        // reset the form
        is_primary.value = 'true'
        address_note.value = ''
        recipient_name.value = ''
        email.value = ''
        phone.value = ''
        address.value = ''
        address_detail.value = ''
        country.value = ''
        province.value = ''
        province_id.value = ''
        city.value = ''
        city_id.value = ''
        postal_code.value = ''
        area_map.value = null
        area_list.value = []
        modalState.value = 'select'
    }

    // add a new address
    const submitAddress = async () => {
        // console.log('submitAddress')
        // Checking data validation country must have value
        if (country.value == '') {
            Swal.fire({
                icon: 'error',
                title: 'Country must be filled',
                showConfirmButton: false,
                timer: 1500
            })
            return
        }

        // if country is Indonesia, then area_map must have value
        if (country.value == 'Indonesia') {
            if (!area_map.value || !area_map.value.id) {
                Swal.fire({
                    icon: 'error',
                    title: 'Missing Area Information',
                    text: 'Please select an area for Indonesian addresses',
                    showConfirmButton: true,
                })
                return
            }
        } else {
            // For non-Indonesian addresses, validate province_id and city_id
            if (province_id.value == '' || province_id.value == null) {
                Swal.fire({
                    icon: 'error',
                    title: 'Missing Province',
                    text: 'Please enter the province',
                    showConfirmButton: true,
                })
                return
            }

            if (city_id.value == '' || city_id.value == null) {
                Swal.fire({
                    icon: 'error',
                    title: 'Missing City',
                    text: 'Please enter the city',
                    showConfirmButton: true,
                })
                return
            }
        }

        const data = {
            is_primary: is_primary.value,
            address_note: address_note.value,
            recipient_name: recipient_name.value,
            email: email.value,
            phone: phone.value,
            address: address.value,
            address_detail: address_detail.value,
            country: country.value,
            province: country.value === 'Indonesia' ? area_map.value?.administrative_division_level_1_name : province.value,
            province_id: country.value === 'Indonesia' ? null : province_id.value.toString(),
            city: country.value === 'Indonesia' ? area_map.value?.administrative_division_level_2_name : city.value,
            city_id: country.value === 'Indonesia' ? null : city_id.value.toString(),
            postal_code: country.value === 'Indonesia' ? area_map.value?.postal_code?.toString() : postal_code.value,
            area_map: country.value === 'Indonesia' ? area_map.value : null
        }

        try {
            if (modalState.value == 'add') {
                const response = await window.axios.post('/api/address', data)
                shipping_address.value = response.data.data

                Swal.fire({
                    icon: 'success',
                    title: 'Address Added!!',
                    showConfirmButton: false,
                    timer: 1500
                })

                setAddressDefault(true)
                modalState.value = 'select'
            } else if (modalState.value == 'update') {
                const response = await window.axios.post(
                    `/api/address/${idAddress.value}`,
                    data
                )
                shipping_address.value = response.data.data
                setAddressDefault(true)
                modalState.value = 'select'
                Swal.fire({
                    icon: 'success',
                    title: 'Address Updated!!',
                    showConfirmButton: false,
                    timer: 1500
                })
            }

            // modalState.value = ''
            getAddresses()
            clearAddress()
        } catch (error: any) {
            console.error('An error occurred:', error)
            Swal.fire({
                icon: 'error',
                title: "Can't add address",
                text: error.response.data.message,
                showConfirmButton: false,
                timer: 2000
            })
        }
    }

    const getExpedition = async () => {
        await useCart().getCart()

        // For Indonesian addresses with area_id, use Biteship v2 API
        if (selected_address.value.country === 'Indonesia' && selected_address.value.area_id) {
            const data = {
                destination: selected_address.value.area_id,
                // weight: await useCart().totalWeight
            }

            try {
                console.log('Calling Biteship v2 expedition with data:', data)
                const response = await window.axios.post(`/api/v2/expedition`, data)
                console.log('Biteship v2 expedition response:', response.data)

                // Handle new response format
                if (response.data.success && response.data.pricing) {
                    expedition_list.value = {
                        results: response.data.pricing,
                        origin: response.data.origin,
                        destination: response.data.destination
                    }
                } else {
                    expedition_list.value = { results: [] }
                }
            } catch (error) {
                console.log('Biteship v2 expedition error:', error)
                expedition_list.value = { results: [] }
            }
        } else {
            // For non-Indonesian addresses or old Indonesian addresses without area_id, use RajaOngkir
            const data = {
                destination: selected_address.value.city_id,
                weight: await useCart().totalWeight
            }

            try {
                const response = await window.axios.post(`/api/expedition`, data)
                expedition_list.value = response.data.rajaongkir
            } catch (error) {
                console.log('RajaOngkir expedition error:', error)
                expedition_list.value = { results: [] }
            }
        }
    }

    const updateSelectedAddress = async (addressId: number) => {
        const selectedAddress = shipping_address.value.find(
            (address: any) => address.id === addressId
        )
        if (selectedAddress) {
            // For Indonesian addresses, strictly require area_id
            if (selectedAddress.country === 'Indonesia' &&
                (!selectedAddress.area_id || selectedAddress.area_id === '')) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid Address',
                    text: 'This Indonesian address is missing area information required for shipping. Please update this address first.',
                    showConfirmButton: true,
                })
                return
            }

            selected_address.value = selectedAddress

            if (selected_address.value.country == 'Indonesia') {
                // For Indonesian addresses, only proceed with area_id
                if (selected_address.value.area_id && selected_address.value.area_id !== '') {
                    await getExpedition()
                } else {
                    // This should not happen due to the check above, but added for safety
                    form_set('update', selected_address.value.id)
                    Swal.fire({
                        icon: 'error',
                        title: 'Missing Area Information',
                        text: 'Please update this address with area information',
                        showConfirmButton: true,
                    })
                    return
                }
            } else {
                // For non-Indonesian addresses, proceed normally
                await getExpedition()
            }
        }
        useCart().haveAddress = true
    }

    const updatePrimaryAddress = async (addressId: number) => {
        try {
            const response = await window.axios.put(
                `/api/address/${addressId}/primary`
            )
            getAddresses()
        } catch (error: any) {
            console.error('An error occurred:', error)
            Swal.fire({
                icon: 'error',
                title: "Can't Set Primary Address",
                text: error.response.data.message,
                showConfirmButton: false,
                timer: 2000
            })
        }
    }

    const form_set = async (type: string, id: any) => {
        if (type == 'update') {
            try {
                const response = await window.axios.get(`/api/address/${id}`)
                is_primary.value = response.data.data.is_primary.toString()
                address_note.value = response.data.data.address_note
                recipient_name.value = response.data.data.recipient_name
                email.value = response.data.data.email
                phone.value = response.data.data.phone
                address.value = response.data.data.address
                address_detail.value = response.data.data.address_detail
                country.value = response.data.data.country
                province.value = response.data.data.province
                province_id.value = response.data.data.province_id
                city.value = response.data.data.city
                city_id.value = response.data.data.city_id
                postal_code.value = response.data.data.postal_code

                // Handle area_map for Indonesian addresses
                if (response.data.data.country === 'Indonesia' && response.data.data.area_id) {
                    area_map.value = {
                        id: response.data.data.area_id,
                        name: response.data.data.area_name || `${response.data.data.city}, ${response.data.data.province}`,
                        administrative_division_level_1_name: response.data.data.province,
                        administrative_division_level_2_name: response.data.data.city,
                        postal_code: response.data.data.postal_code
                    }
                } else {
                    area_map.value = null
                }

                idAddress.value = id
                modalState.value = type

                if (response.data.data.country === 'Indonesia') {
                    // For Indonesian addresses, don't call getCity since we use area_map
                } else {
                    getCity()
                }
            } catch (error) {
                console.error(error)
            }
        } else if (type == 'add') {
            if (shipping_address.value.length == 0) {
                is_primary.value = 'true'
            } else {
                is_primary.value = 'false'
            }
            modalState.value = 'add'

            address_note.value = ''
            recipient_name.value = ''
            email.value = ''
            phone.value = ''
            address.value = ''
            address_detail.value = ''
            country.value = ''
            province.value = ''
            city.value = ''
            postal_code.value = ''
            area_map.value = null
            area_list.value = []
        } else if (type == 'select') {
            modalState.value = type
        } else if (shipping_address.value.length == 0) {
            modalState.value = 'add'
        } else {
            modalState.value = ''
        }
    }

    const deleteAddress = async (addressId: number) => {
        // Confirm delete
        Swal.fire({
            icon: 'question',
            title: 'Delete Address?',
            showCancelButton: true,
            confirmButtonText: 'Delete'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await window.axios.delete(`/api/address/${addressId}`)
                    getAddresses()
                    Swal.fire({
                        icon: 'success',
                        title: 'Address Deleted!!',
                        showConfirmButton: false,
                        timer: 1500
                    })
                } catch (error: any) {
                    console.error('An error occurred:', error)
                    Swal.fire({
                        icon: 'error',
                        title: "Can't Delete Address",
                        text: error.response.data.message,
                        showConfirmButton: false,
                        timer: 2000
                    })
                }
            }
        })
    }

    return {
        idAddress,
        shipping_note,
        selected_address,
        shipping_address,
        modalState,
        is_primary,
        address_note,
        recipient_name,
        email,
        phone,
        address,
        address_detail,
        country,
        province,
        province_id,
        city,
        city_id,
        postal_code,
        area_map,
        area_list,
        province_list,
        city_list,
        expedition_list,
        selected_expedition,
        shipping_information,
        getShippingInformation,
        getProvince,
        getCity,
        getAreaMap,
        setAddressDefault,
        getAddresses,
        submitAddress,
        form_set,
        updatePrimaryAddress,
        updateSelectedAddress,
        deleteAddress,
        clearAddress
    }
})
